import { type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DEMO } from '../lib/demo';
import { useExpediente } from '../lib/hooks';
import { CADENCIA, RIESGO_LABEL } from '../lib/phase1';
import { ArrowLeft, Printer } from 'lucide-react';

/**
 * Print-optimized expediente dossier (RPT-01) — the regulatory artifact.
 * Rendered outside the app shell so it prints clean; "Imprimir / Guardar PDF"
 * uses the browser print dialog (→ Save as PDF).
 */
export default function Dossier() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: e, isLoading } = useExpediente(id);
  const generatedDate = new Date().toLocaleDateString('es-PA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  if (isLoading || !e) {
    return <div className="grid min-h-screen place-items-center text-ink-faint">Cargando dossier…</div>;
  }

  const Th = ({ children }: { children: ReactNode }) => (
    <th className="border-b border-line px-3 py-2 text-left font-mono text-[9px] uppercase tracking-wider text-ink-faint">
      {children}
    </th>
  );
  const Td = ({ children }: { children: ReactNode }) => (
    <td className="border-b border-line px-3 py-2 align-top">{children}</td>
  );
  const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <section className="mt-7">
      <h2 className="mb-2 border-b-2 border-ink pb-1 font-serif text-base font-semibold">{title}</h2>
      {children}
    </section>
  );

  return (
    <div className="min-h-screen bg-canvas-alt py-8">
      {/* toolbar — hidden on print */}
      <div className="no-print mx-auto mb-4 flex max-w-[800px] items-center justify-between px-4">
        <button
          onClick={() => navigate(`/expedientes/${e.id}`)}
          className="flex items-center gap-1.5 font-mono text-[11px] text-ink-soft hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Volver al expediente
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_14px_-6px_rgba(138,46,60,.6)] hover:bg-primary-dark"
        >
          <Printer className="h-4 w-4" strokeWidth={1.75} /> Imprimir / Guardar PDF
        </button>
      </div>

      {/* the document */}
      <article className="dossier mx-auto max-w-[800px] bg-white px-12 py-10 text-[13px] text-ink shadow-card">
        {/* masthead */}
        <header className="flex items-start justify-between border-b-2 border-ink pb-4">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary font-serif text-lg font-semibold text-white">
              C
            </span>
            <div>
              <div className="font-serif text-xl font-medium">Cumpli</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-faint">
                Expediente de Cumplimiento · AML/CFT
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="inline-block rounded border border-red-300 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-red-600">
              Confidencial · Ley 23
            </div>
            <div className="mt-1 font-mono text-[10px] text-ink-faint">{e.id}</div>
            <div className="font-mono text-[10px] text-ink-faint">Generado {generatedDate}</div>
          </div>
        </header>

        {/* client */}
        <div className="mt-5">
          <h1 className="font-serif text-2xl font-medium">{e.name}</h1>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-ink-soft">
            <span>{e.kind}</span>
            <span>{e.docId}</span>
            <span>Sector: {e.sector}</span>
            <span>Estado: {e.estado}</span>
            <span>
              Riesgo: {RIESGO_LABEL[e.riesgo]}
              {e.pep ? ' · PEP' : ''}
            </span>
          </div>
        </div>

        <Section title="1 · Identificación">
          <table className="w-full">
            <tbody>
              {e.fields.map((f) => (
                <tr key={f.k}>
                  <Td>
                    <span className="font-mono text-[10px] uppercase tracking-wide text-ink-faint">{f.k}</span>
                  </Td>
                  <Td>
                    <span className="font-semibold">{f.v}</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="2 · Documentos">
          <table className="w-full">
            <thead>
              <tr>
                <Th>Documento</Th>
                <Th>Categoría</Th>
                <Th>Vence</Th>
                <Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {e.docs.length === 0 && (
                <tr>
                  <Td>Sin documentos cargados.</Td>
                </tr>
              )}
              {e.docs.map((d, i) => (
                <tr key={i}>
                  <Td>
                    <b>{d.name}</b>
                  </Td>
                  <Td>{d.category}</Td>
                  <Td>{d.expiry ?? '—'}</Td>
                  <Td>{d.estado}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="3 · Validaciones automáticas">
          <table className="w-full">
            <thead>
              <tr>
                <Th>Validación</Th>
                <Th>Proveedor</Th>
                <Th>Resultado</Th>
              </tr>
            </thead>
            <tbody>
              {e.validations.map((v) => (
                <tr key={v.key}>
                  <Td>
                    <b>{v.title}</b>
                  </Td>
                  <Td>{v.provider}</Td>
                  <Td>{v.result}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="4 · Evaluación de riesgo">
          <p className="mb-2">
            Nivel <b>{RIESGO_LABEL[e.riesgo]}</b> (puntaje {e.score}/100). Próxima actualización:{' '}
            <b>{CADENCIA[e.riesgo]}</b>.{e.pep ? ' PEP detectado → revisión manual y EDD requeridas.' : ''}
          </p>
          {e.factors.length > 0 && (
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Factor</Th>
                  <Th>Fuente</Th>
                  <Th>Peso</Th>
                </tr>
              </thead>
              <tbody>
                {e.factors.map((f, i) => (
                  <tr key={i}>
                    <Td>{f.name}</Td>
                    <Td>{f.source}</Td>
                    <Td>{f.weight}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {e.ubo && e.ubo.length > 0 && (
          <Section title="5 · Beneficiarios finales y estructura">
            <table className="w-full">
              <thead>
                <tr>
                  <Th>Nombre</Th>
                  <Th>Rol</Th>
                  <Th>Diligencia</Th>
                </tr>
              </thead>
              <tbody>
                {e.ubo.map((u, i) => (
                  <tr key={i}>
                    <Td>
                      <b>{u.name}</b>
                    </Td>
                    <Td>{u.role}</Td>
                    <Td>{u.detail}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        <Section title={`${e.ubo && e.ubo.length > 0 ? '6' : '5'} · Bitácora`}>
          <table className="w-full">
            <tbody>
              {e.history.map((h, i) => (
                <tr key={i}>
                  <Td>
                    <span className="font-mono text-[10px] text-ink-faint">{h.ts}</span>
                  </Td>
                  <Td>
                    <b>{h.actor}</b> <span className="text-ink-faint">({h.role})</span> {h.action}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <footer className="mt-8 border-t border-line pt-3 font-mono text-[9px] text-ink-faint">
          Generado por Cumpli · {generatedDate} · Documento para inspección regulatoria (UAF).
          {DEMO && ' · Entorno demo — datos de demostración.'}
        </footer>
      </article>
    </div>
  );
}
